import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listStories, getStory, getSkit, SableApiError, } from "../api/sableApi";
import { useAudioCurrentTime } from "./useAudioCurrentTime";
const LANGUAGES = [
    { code: "en", label: "English", available: true },
    { code: "es", label: "Español", available: false },
    { code: "pt", label: "Português", available: false },
];
export default function StorytellerExperience({ token, directory }) {
    const [searchParams] = useSearchParams();
    const deepLinkStoryId = searchParams.get("story");
    const [screen, setScreen] = useState(deepLinkStoryId ? "player" : directory.introAudioUrl ? "intro" : "list");
    const [stories, setStories] = useState(null);
    const [error, setError] = useState(null);
    const [activeStory, setActiveStory] = useState(null);
    const [activeSeries, setActiveSeries] = useState(null);
    const [langPickerOpen, setLangPickerOpen] = useState(false);
    const rootStyle = useMemo(() => ({ "--accent": directory.accent ?? "#2f6f7d" }), [directory.accent]);
    useEffect(() => {
        let cancelled = false;
        listStories(token)
            .then((rows) => {
            if (cancelled)
                return;
            setStories(rows);
            // A "Preview" link from the owner console (?story=<document_id>) jumps straight to that
            // story's player instead of the intro/list screens, since the owner is previewing one
            // specific piece of content, not browsing like a visitor would.
            if (deepLinkStoryId) {
                const match = rows.find((r) => String(r.documentId) === deepLinkStoryId);
                if (match) {
                    setActiveStory(match);
                    setScreen("player");
                }
            }
        })
            .catch((err) => {
            if (cancelled)
                return;
            setError(err instanceof SableApiError ? err.message : "Couldn't load stories.");
        });
        return () => {
            cancelled = true;
        };
    }, [token, deepLinkStoryId]);
    return (_jsx("div", { className: "ac-root", style: rootStyle, children: _jsxs("div", { className: "ac-shell st-shell", children: [_jsxs("header", { className: "st-header", children: [_jsxs("div", { className: "st-brand", children: [_jsx("div", { className: "ac-avatar", children: (directory.ownerName ?? "?").slice(0, 1) }), _jsxs("div", { children: [_jsx("div", { className: "st-name", children: directory.ownerName }), _jsx("div", { className: "ac-mono st-tagline", children: directory.business })] })] }), _jsx("button", { className: "st-icon-btn", "aria-label": "Language", onClick: () => setLangPickerOpen((v) => !v), children: "\uD83C\uDF10" })] }), langPickerOpen && (_jsx("div", { className: "st-lang-picker", children: LANGUAGES.map((l) => (_jsxs("button", { className: "st-lang-row", disabled: !l.available, onClick: () => setLangPickerOpen(false), children: [l.label, !l.available && _jsx("span", { className: "ac-mono st-soon", children: "coming soon" })] }, l.code))) })), screen === "intro" && (_jsx(IntroScreen, { audioUrl: directory.introAudioUrl, ownerName: directory.ownerName, onDone: () => setScreen("list") })), screen === "list" && (_jsx(StoryListScreen, { stories: stories, error: error, onPickSeries: (series) => {
                        setActiveSeries(series);
                        setScreen("category");
                    }, onPick: (s) => {
                        setActiveStory(s);
                        setScreen("player");
                    } })), screen === "category" && stories && (_jsx(CategoryScreen, { series: activeSeries, stories: activeSeries === ALL_STORIES ? stories : stories.filter((s) => s.series === activeSeries), onBack: () => setScreen("list"), onPick: (s) => {
                        setActiveStory(s);
                        setScreen("player");
                    } })), screen === "player" && activeStory && (_jsx(StoryPlayerScreen, { token: token, storyListItem: activeStory, onBack: () => setScreen("list"), onViewStories: () => setScreen("list") }))] }) }));
}
function IntroScreen({ audioUrl, ownerName, onDone }) {
    const audioRef = useRef(null);
    useEffect(() => {
        const el = audioRef.current;
        if (!el)
            return;
        el.play().catch(() => {
            /* Autoplay can be blocked -- the visible "Continue" button below still works either way. */
        });
    }, []);
    return (_jsxs("div", { className: "st-intro", children: [_jsx("audio", { ref: audioRef, src: audioUrl, onEnded: onDone }), _jsx("div", { className: "st-intro-pulse", children: "\uD83C\uDF99\uFE0F" }), _jsxs("p", { className: "st-intro-text", children: [ownerName ?? "Your storyteller", " is saying hello..."] }), _jsx("button", { className: "st-btn st-btn-primary", onClick: onDone, children: "Continue" })] }));
}
function StoryListScreen({ stories, error, onPickSeries, onPick, }) {
    if (error)
        return _jsx("div", { className: "st-empty", children: error });
    if (stories === null)
        return _jsx("div", { className: "st-empty", children: "Loading stories\u2026" });
    if (stories.length === 0)
        return _jsx("div", { className: "st-empty", children: "No stories yet." });
    // Distinct series names, in the order they first appear in the Assistant's own curated
    // `story_document_ids` list -- each becomes a single category button (tapping it opens
    // CategoryScreen, which lists just that series' stories in the same curated order). `series`
    // (the collection, e.g. "Baptism") is distinct from each story's own `topic` (a one-line theme)
    // -- see sableApi.ts. Stories with no series (existing Assistants built before series grouping
    // existed) never get a category button -- they only ever show in "All stories" below, so this
    // is backward compatible rather than a breaking change.
    const seriesNames = [];
    for (const s of stories) {
        if (s.series && !seriesNames.includes(s.series))
            seriesNames.push(s.series);
    }
    return (_jsx("div", { className: "st-list", children: _jsxs("div", { className: "st-category-group", children: [seriesNames.map((series) => {
                    const count = stories.filter((s) => s.series === series).length;
                    return (_jsxs("button", { className: "st-category-card", onClick: () => onPickSeries(series), children: [_jsx("div", { className: "st-category-title", children: series }), _jsxs("div", { className: "ac-mono st-category-count", children: [count, " ", count === 1 ? "story" : "stories"] })] }, series));
                }), _jsxs("button", { className: "st-category-card", onClick: () => onPickSeries(ALL_STORIES), children: [_jsx("div", { className: "st-category-title", children: "All stories" }), _jsxs("div", { className: "ac-mono st-category-count", children: [stories.length, " ", stories.length === 1 ? "story" : "stories"] })] })] }) }));
}
const ALL_STORIES = "__all_stories__";
function CategoryScreen({ series, stories, onBack, onPick, }) {
    return (_jsxs("div", { className: "st-list", children: [_jsx("div", { className: "st-player-actions", children: _jsx("button", { className: "st-icon-btn", "aria-label": "Back to stories", onClick: onBack, children: "\u2190" }) }), _jsxs("div", { className: "st-topic-group", children: [_jsx("p", { className: "ac-mono st-list-label", children: series === ALL_STORIES ? "All stories" : series }), stories.map((s) => (_jsxs("button", { className: "st-story-card", onClick: () => onPick(s), children: [_jsx("div", { className: "st-story-title", children: s.title }), s.scriptureReference && _jsx("div", { className: "ac-mono st-story-ref", children: s.scriptureReference })] }, s.documentId)))] })] }));
}
function StoryPlayerScreen({ token, storyListItem, onBack, onViewStories, }) {
    const [story, setStory] = useState(null);
    const [error, setError] = useState(null);
    const [scriptureOpen, setScriptureOpen] = useState(false);
    const [format, setFormat] = useState("story");
    const audioRef = useRef(null);
    const currentTime = useAudioCurrentTime(audioRef, Boolean(story?.audioUrl));
    useEffect(() => {
        let cancelled = false;
        getStory(token, storyListItem.documentId)
            .then((s) => {
            if (!cancelled)
                setStory(s);
        })
            .catch((err) => {
            if (cancelled)
                return;
            setError(err instanceof SableApiError ? err.message : "Couldn't load this story.");
        });
        return () => {
            cancelled = true;
        };
    }, [token, storyListItem.documentId]);
    const activeWordIndex = useMemo(() => {
        if (!story)
            return -1;
        for (let i = story.wordTimestamps.length - 1; i >= 0; i--) {
            if (currentTime >= story.wordTimestamps[i].start)
                return i;
        }
        return -1;
    }, [story, currentTime]);
    if (error)
        return _jsx("div", { className: "st-empty", children: error });
    if (story === null)
        return _jsx("div", { className: "st-empty", children: "Loading story\u2026" });
    const hasTimestamps = story.wordTimestamps.length > 0;
    return (_jsxs("div", { className: "st-player", children: [_jsxs("div", { className: "st-player-actions", children: [_jsx("button", { className: "st-icon-btn", "aria-label": "Back to stories", onClick: onBack, children: "\u2190" }), _jsx("button", { className: "st-icon-btn", "aria-label": "View stories", onClick: onViewStories, children: "\uD83D\uDCDA" }), story.scriptureText && (_jsx("button", { className: "st-icon-btn", "aria-label": "View scripture", onClick: () => setScriptureOpen((v) => !v), children: "\uD83D\uDCD6" }))] }), _jsx("h1", { className: "st-player-title", children: story.title }), scriptureOpen && story.scriptureText && (_jsxs("div", { className: "st-scripture-box", children: [_jsx("div", { className: "ac-mono st-story-ref", children: story.scriptureReference }), _jsx("p", { className: "st-scripture-text", children: story.scriptureText })] })), (story.hasSkit || story.lessonText || story.questions.length > 0) && (_jsxs("div", { className: "st-format-toggle", role: "tablist", children: [_jsx("button", { className: format === "story" ? "st-format-btn st-format-active" : "st-format-btn", onClick: () => setFormat("story"), children: "Story" }), story.hasSkit && (_jsx("button", { className: format === "skit" ? "st-format-btn st-format-active" : "st-format-btn", onClick: () => setFormat("skit"), children: "Skit" })), story.lessonText && (_jsx("button", { className: format === "lesson" ? "st-format-btn st-format-active" : "st-format-btn", onClick: () => setFormat("lesson"), children: "Lesson" })), story.questions.length > 0 && (_jsx("button", { className: format === "questions" ? "st-format-btn st-format-active" : "st-format-btn", onClick: () => setFormat("questions"), children: "Questions" }))] })), format === "skit" && _jsx(SkitView, { token: token, documentId: storyListItem.documentId, title: story.title }), format === "lesson" && _jsx(LessonView, { text: story.lessonText ?? "" }), format === "questions" && _jsx(QuestionsView, { questions: story.questions }), format === "story" && _jsx(StoryFormatView, { story: story, audioRef: audioRef, activeWordIndex: activeWordIndex })] }));
}
// Lesson/Questions are plain, read-only text -- no narration (per the user's own direction,
// 2026-10-14) -- so these are deliberately simpler than StoryFormatView/SkitView, no audio
// element or word-highlighting state at all.
function LessonView({ text }) {
    return _jsx("div", { className: "st-story-text st-lesson-text", children: text });
}
// A simple scored multiple-choice quiz (per the user's own direction: "keep the questions
// simple but it will give them a score") -- one question at a time, tap an option to lock in an
// answer (shows right/wrong immediately), then Next; a final score screen with a Try Again
// reset. `correctIndex` comes straight from the API response (see QuizQuestionOut's own note on
// why that's fine for a kids' quiz) -- scoring is purely client-side, nothing is submitted back.
function QuestionsView({ questions }) {
    const [index, setIndex] = useState(0);
    const [selected, setSelected] = useState(null);
    const [score, setScore] = useState(0);
    if (questions.length === 0)
        return _jsx("div", { className: "st-empty", children: "No questions yet." });
    if (index >= questions.length) {
        return (_jsxs("div", { className: "st-quiz-done", children: [_jsxs("p", { className: "st-quiz-score", children: ["You got ", score, " out of ", questions.length, "!"] }), _jsx("button", { className: "st-format-btn", onClick: () => {
                        setIndex(0);
                        setSelected(null);
                        setScore(0);
                    }, children: "Try Again" })] }));
    }
    const q = questions[index];
    function choose(optionIndex) {
        if (selected !== null)
            return; // already answered this one
        setSelected(optionIndex);
        if (optionIndex === q.correctIndex)
            setScore((s) => s + 1);
    }
    return (_jsxs("div", { className: "st-quiz", children: [_jsxs("p", { className: "ac-mono st-quiz-progress", children: ["Question ", index + 1, " of ", questions.length] }), _jsx("p", { className: "st-quiz-question", children: q.question }), _jsx("div", { className: "st-quiz-options", children: q.options.map((opt, i) => {
                    let cls = "st-quiz-option";
                    if (selected !== null) {
                        if (i === q.correctIndex)
                            cls += " st-quiz-option-correct";
                        else if (i === selected)
                            cls += " st-quiz-option-incorrect";
                    }
                    return (_jsx("button", { className: cls, onClick: () => choose(i), disabled: selected !== null, children: opt }, i));
                }) }), selected !== null && (_jsx("button", { className: "st-format-btn st-quiz-next", onClick: () => { setIndex((i) => i + 1); setSelected(null); }, children: index + 1 < questions.length ? "Next" : "See Score" }))] }));
}
function StoryFormatView({ story, audioRef, activeWordIndex, }) {
    // Real bug found 2026-10-14: rendering the text AS `wordTimestamps` (instead of as the real
    // `storyText`, using timestamps only to highlight) silently drops any word the TTS's forced-
    // alignment step failed to confidently time -- which happens scattered throughout longer
    // narrations, not just at the edges. The underlying story text and audio are both complete;
    // only this render path corrupted what was displayed by treating a best-effort alignment as
    // the authoritative word list. Only trust word-level highlighting when the counts actually
    // match 1:1 (a reliable alignment) -- otherwise show the real, complete text with no
    // highlighting rather than a garbled, word-dropping one.
    const textWords = useMemo(() => story.storyText.split(/\s+/).filter(Boolean), [story.storyText]);
    const canHighlight = story.wordTimestamps.length > 0 && story.wordTimestamps.length === textWords.length;
    return (_jsxs(_Fragment, { children: [story.audioUrl && _jsx("audio", { ref: audioRef, src: story.audioUrl, controls: true, className: "st-audio" }), !story.audioUrl && _jsx("p", { className: "ac-mono st-no-audio", children: "Narration isn't ready for this story yet." }), _jsx("div", { className: "st-story-text", children: canHighlight
                    ? // The real narration audio reads the story text AND the closing line as one
                        // continuous recording, so `wordTimestamps` already covers both -- rendered here as a
                        // single highlighted flow rather than splitting them into two visually separate blocks
                        // (which would either duplicate the closing line or leave its words unhighlighted).
                        textWords.map((w, i) => (_jsxs("span", { className: i === activeWordIndex ? "st-word st-word-active" : "st-word", children: [w, " "] }, i)))
                    : story.storyText }), !canHighlight && story.closingLine && _jsx("p", { className: "st-closing-line", children: story.closingLine })] }));
}
function SkitView({ token, documentId, title }) {
    const [skit, setSkit] = useState(null);
    const [error, setError] = useState(null);
    const audioRef = useRef(null);
    const currentTime = useAudioCurrentTime(audioRef, Boolean(skit?.audioUrl));
    useEffect(() => {
        let cancelled = false;
        getSkit(token, documentId)
            .then((s) => {
            if (!cancelled)
                setSkit(s);
        })
            .catch((err) => {
            if (cancelled)
                return;
            setError(err instanceof SableApiError && err.status === 404 ? "No skit for this story yet." : "Couldn't load the skit.");
        });
        return () => {
            cancelled = true;
        };
    }, [token, documentId]);
    const activeLineIndex = useMemo(() => {
        if (!skit)
            return -1;
        for (let i = skit.lines.length - 1; i >= 0; i--) {
            if (currentTime >= skit.lines[i].start)
                return i;
        }
        return -1;
    }, [skit, currentTime]);
    const activeWordIndex = useMemo(() => {
        if (!skit || activeLineIndex < 0)
            return -1;
        const words = skit.lines[activeLineIndex].wordTimestamps;
        for (let i = words.length - 1; i >= 0; i--) {
            if (currentTime >= words[i].start)
                return i;
        }
        return -1;
    }, [skit, activeLineIndex, currentTime]);
    // One color per character (per the user's own direction: "color coded to the character so it is
    // easy to see who is speaking"), assigned in the order each character first appears so it stays
    // stable across renders of the same skit. Cycles if a skit somehow has more characters than the
    // palette -- unlikely for a short kids' skit, but never breaks instead of erroring.
    const characterColors = useMemo(() => {
        if (!skit)
            return {};
        const palette = ["#2f6f7d", "#b2461e", "#6a4c93", "#1f7a4d", "#a4243b", "#1b5299", "#8a6d00"];
        const colors = {};
        Object.keys(skit.characters).forEach((name, i) => {
            colors[name] = palette[i % palette.length];
        });
        return colors;
    }, [skit]);
    if (error)
        return _jsx("div", { className: "st-empty", children: error });
    if (skit === null)
        return _jsx("div", { className: "st-empty", children: "Loading skit\u2026" });
    return (_jsxs("div", { className: "st-skit", children: [skit.audioUrl ? (_jsx("audio", { ref: audioRef, src: skit.audioUrl, controls: true, className: "st-audio" })) : (_jsx("p", { className: "ac-mono st-no-audio", children: "Narration isn't ready for this skit yet." })), _jsxs("div", { className: "st-skit-cast ac-mono", children: ["Cast:", " ", Object.keys(skit.characters).map((name, i) => (_jsxs("span", { className: "st-cast-chip", style: { "--char-color": characterColors[name] }, children: [i > 0 && " · ", _jsx("span", { className: "st-cast-dot" }), name] }, name)))] }), _jsx("div", { className: "st-skit-lines", children: skit.lines.map((line, i) => (_jsxs("div", { className: i === activeLineIndex ? "st-skit-line st-skit-line-active" : "st-skit-line", style: { "--char-color": characterColors[line.character] }, children: [_jsx("div", { className: "st-skit-character", children: line.character }), _jsx("div", { className: "st-skit-text", children: i === activeLineIndex &&
                                line.wordTimestamps.length > 0 &&
                                line.wordTimestamps.length === line.text.split(/\s+/).filter(Boolean).length
                                ? line.wordTimestamps.map((w, j) => (_jsxs("span", { className: j === activeWordIndex ? "st-word st-word-active" : "st-word", children: [w.word, " "] }, j)))
                                : line.text })] }, i))) })] }));
}
